import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { postDownloadWithProgress } from "@/lib/downloadWithProgress";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { FileText, Download, Paperclip, PenTool } from "lucide-react";

/**
 * 🆕 Feb 2026 — Bottone riutilizzabile "Compila da modello PDF"
 * Apre un dialog con la lista dei modelli PDF configurati, permette
 * di sceglierne uno e lo applica al target (policy o quote).
 * Il PDF risultante viene salvato come attachment esistente.
 *
 * Props:
 *  - policyId?: string
 *  - quoteId?: string
 *  - variant?: "outline" | "default" (default "outline")
 *  - onApplied?: (attachment) => void  → callback dopo salvataggio (per refresh lista allegati)
 *  - testid?: string
 */
export default function ApplyPdfTemplateButton({ policyId, quoteId, variant = "outline", onApplied, testid, label, iconOnly = false, open: openProp, onOpenChange, hideTrigger = false }) {
  const [openInternal, setOpenInternal] = useState(false);
  // 🆕 Feb 2026 — supporto uso "controllato": consente di aprire il dialog da un
  // menu esterno (es. DropdownMenu su Policies) senza il bottone di trigger.
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openInternal;
  const setOpen = (v) => {
    if (isControlled) onOpenChange && onOpenChange(v);
    else setOpenInternal(v);
  };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState("");
  const [filenames, setFilenames] = useState({}); // 🆕 { [templateId]: filename }

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/admin/pdf-templates")
      .then((r) => {
        const list = r.data || [];
        setItems(list);
        // Pre-popola i nomi file con il nome del template
        const map = {};
        list.forEach((t) => { map[t.id] = t.name || "documento"; });
        setFilenames(map);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open]);

  const applyOne = async (tpl, download = false) => {
    setApplying(tpl.id + (download ? "-dl" : "-att"));
    try {
      const chosen = (filenames[tpl.id] || tpl.name || "documento").trim();
      const body = policyId ? { policy_id: policyId } : { quote_id: quoteId };
      body.save_as_attachment = !download;
      body.filename = chosen;
      if (download) {
        // 🆕 Feb 2026 — Progress reale con barra globale (simulato 0→80% durante generazione,
        // poi progress reale 80→100% durante lo stream del blob).
        const finalName = chosen.toLowerCase().endsWith(".pdf") ? chosen : `${chosen}.pdf`;
        await postDownloadWithProgress({
          label: `Generazione PDF: ${tpl.name}`,
          filename: finalName,
          run: (cfg) => api.post(`/admin/pdf-templates/${tpl.id}/apply`, body, cfg),
        });
        toast.success("Documento scaricato");
      } else {
        const { data } = await api.post(`/admin/pdf-templates/${tpl.id}/apply`, body);
        toast.success(`Salvato negli allegati: ${data.attachment?.original_filename || "PDF"}`);
        onApplied && onApplied(data.attachment);
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setApplying(""); }
  };

  const sendForSignature = async (tpl) => {
    setApplying(tpl.id + "-sig");
    try {
      const chosen = (filenames[tpl.id] || tpl.name || "documento").trim();
      const body = policyId ? { policy_id: policyId } : { quote_id: quoteId };
      body.filename = chosen;
      const { data } = await api.post(`/admin/pdf-templates/${tpl.id}/send-for-signature`, body);
      toast.success(`Documento pronto per la firma (${data.signature_placements_count} campo/i firma). Vai nel Centro Firme per inviarlo.`);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setApplying(""); }
  };

  if (!policyId && !quoteId) return null;

  return (
    <>
      {!hideTrigger && (
        <Button variant={variant} size="sm" onClick={() => setOpen(true)} data-testid={testid || "apply-pdf-tpl-btn"} className="text-xs">
          <FileText className="w-3.5 h-3.5 mr-1" /> {label || "Genera documento da modello"}
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="apply-pdf-tpl-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-emerald-600" /> Genera documento da modello</DialogTitle>
            <DialogDescription>Seleziona un modello. Puoi scaricare direttamente il PDF compilato oppure salvarlo tra gli allegati.</DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="py-8 text-center text-slate-400 text-sm">Caricamento…</div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              Nessun modello configurato. Vai in <em>Impost. Gestionale → Modelli PDF – Policy/Quote</em> per crearne uno.
            </div>
          ) : (
            <ul className="space-y-2 max-h-80 overflow-y-auto">
              {items.map((t) => (
                <li key={t.id} className="rounded-md border border-slate-200 bg-white p-2.5 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{t.name}</div>
                      <div className="text-[10px] uppercase font-bold text-emerald-700">{t.mode === "visual" ? "📐 Visuale" : "📝 Testo"}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => applyOne(t, true)} disabled={applying.startsWith(t.id)} data-testid={`apply-tpl-dl-${t.id}`} title="Scarica PDF">
                        <Download className="w-3.5 h-3.5" /> <span className="hidden sm:inline ml-1">Scarica</span>
                      </Button>
                      <Button size="sm" onClick={() => applyOne(t, false)} disabled={applying.startsWith(t.id)} data-testid={`apply-tpl-${t.id}`} title="Salva negli allegati" className="bg-emerald-600 hover:bg-emerald-700">
                        <Paperclip className="w-3.5 h-3.5" /> <span className="hidden sm:inline ml-1">Salva</span>
                      </Button>
                      {t.mode === "visual" && (t.placements || []).some((p) => p.token === "signature") && (
                        <Button size="sm" onClick={() => sendForSignature(t)} disabled={applying.startsWith(t.id)} data-testid={`apply-tpl-sig-${t.id}`} title="Invia al Centro Firme" className="bg-indigo-600 hover:bg-indigo-700">
                          <PenTool className="w-3.5 h-3.5" /> <span className="hidden sm:inline ml-1">Firma</span>
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] text-slate-500 whitespace-nowrap">Nome file:</label>
                    <Input
                      value={filenames[t.id] ?? ""}
                      onChange={(e) => setFilenames({ ...filenames, [t.id]: e.target.value })}
                      className="h-7 text-xs"
                      placeholder="es. Preventivo_Rossi"
                      data-testid={`apply-tpl-filename-${t.id}`}
                    />
                    <span className="text-[10px] text-slate-400 whitespace-nowrap">.pdf</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
